import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillingSummary = ({ bills, onPayBill, onViewStatement }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'overdue':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const totalBalance = bills?.reduce((sum, bill) => sum + bill?.amount, 0);

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-success/10 rounded-lg">
            <Icon name="DollarSign" size={20} color="var(--color-success)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Billing Summary</h2>
            <p className="text-xs md:text-sm text-muted-foreground">Account balance and statements</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="FileText"
          iconPosition="left"
          onClick={onViewStatement}
        >
          <span className="hidden md:inline">View Statement</span>
          <span className="md:hidden">Statement</span>
        </Button>
      </div>
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
        <p className="text-xs md:text-sm text-muted-foreground mb-2">Total Balance Due</p>
        <p className="text-2xl md:text-3xl font-bold text-foreground mb-4">{formatCurrency(totalBalance)}</p>
        <Button
          variant="default"
          size="sm"
          iconName="CreditCard"
          iconPosition="left"
          onClick={() => onPayBill(null)}
          fullWidth
        >
          Make Payment
        </Button>
      </div>
      <div className="space-y-3">
        {bills?.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full mx-auto mb-4">
              <Icon name="CheckCircle2" size={32} className="text-success" />
            </div>
            <p className="text-sm md:text-base text-muted-foreground">No outstanding bills</p>
          </div>
        ) : (
          bills?.map((bill) => (
            <div
              key={bill?.id}
              className="bg-background rounded-lg border border-border p-4"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm md:text-base font-semibold text-foreground">{bill?.description}</h3>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(bill?.status)}`}>
                      {bill?.statusLabel}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground mb-2">
                    <div className="flex items-center gap-1.5">
                      <Icon name="Calendar" size={14} />
                      <span>Date: {formatDate(bill?.date)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name="Clock" size={14} />
                      <span>Due: {formatDate(bill?.dueDate)}</span>
                    </div>
                  </div>
                  <p className="text-lg md:text-xl font-bold text-foreground">{formatCurrency(bill?.amount)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {bill?.status !== 'paid' && (
                    <Button
                      variant="default"
                      size="sm"
                      iconName="CreditCard"
                      iconPosition="left"
                      onClick={() => onPayBill(bill)}
                      className="flex-1 md:flex-none"
                    >
                      Pay Now
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    className="flex-1 md:flex-none"
                  >
                    <span className="hidden md:inline">Download</span>
                    <span className="md:hidden">PDF</span>
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BillingSummary;